<template>
  <section class="admin-content" id="disease-tracking">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between flex-wrap text-white" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-thermometer" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Disease Tracking</h4>
                <small style="opacity: 0.8;">Diagnoses the agency follows: who has them, what they cost, where and when they appear</small>
              </div>
            </div>
            <button v-if="canManage" class="btn filter-toggle-btn" @click="openForm(null)"><i class="fe fe-plus mr-1"></i> Track a disease</button>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <!-- Add / edit -->
          <div v-if="form" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-edit-2" style="font-size: 12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">{{ form.id ? `Edit ${form.name}` : "Track a disease" }}</h6>
            </div>
            <div class="card-body">
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Name</label>
                  <input class="form-control" v-model="form.name" placeholder="e.g. Malaria" />
                </div>
                <div class="form-group col-md-8">
                  <label class="small font-weight-bold text-secondary">Diagnoses counted as this disease <span class="text-muted font-weight-normal">({{ form.diagnoses.length }} chosen)</span></label>
                  <DiagnosisPicker :key="form.id || 'new'" v-model="form.diagnoses" :loading-chosen="loadingChosen" @input="nameFromDiagnoses" />
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Kind of disease</label>
                  <select class="form-control" v-model="form.is_chronic">
                    <option :value="false">Acute (people recover and can catch it again)</option>
                    <option :value="true">Chronic (once diagnosed, always a sufferer)</option>
                  </select>
                </div>
                <div class="form-group col-md-4" v-if="!form.is_chronic">
                  <label class="small font-weight-bold text-secondary">A new sufferer has had no case for</label>
                  <select class="form-control" v-model.number="form.new_case_days">
                    <option :value="30">30 days</option><option :value="90">3 months</option><option :value="180">6 months</option><option :value="365">12 months</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Category</label>
                  <select class="form-control" v-model="form.category">
                    <option :value="null">Automatic (from the data)</option>
                    <option v-for="c in categories" :key="c.key" :value="c.key">{{ c.label }}: {{ c.description }}</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Alerts</label>
                  <div class="form-check"><input id="a1" type="checkbox" class="form-check-input" v-model="form.alert_new_cases" /><label for="a1" class="form-check-label small">New sufferers: in-app as they are recorded, email daily</label></div>
                  <div class="form-check"><input id="a2" type="checkbox" class="form-check-input" v-model="form.alert_spikes" /><label for="a2" class="form-check-label small">Higher than usual for the time of year</label></div>
                </div>
                <div class="form-group col-md-8">
                  <label class="small font-weight-bold text-secondary">Tag staff to alert (empty = agency admins)</label>
                  <v-select multiple v-model="form.watchers" :options="staff" label="label" :reduce="(s) => s.id" placeholder="Choose staff…" />
                </div>
                <div class="form-group col-md-12">
                  <label class="small font-weight-bold text-secondary">Notes</label>
                  <textarea class="form-control" rows="2" v-model="form.notes"></textarea>
                </div>
              </div>
              <div class="d-flex" style="gap: 8px;">
                <button class="btn btn-submit" @click="save" :disabled="saving || loadingChosen || !form.name || !form.diagnoses.length">{{ saving ? "Saving…" : "Save" }}</button>
                <button class="btn btn-outline-secondary" @click="form = null; if ($route.query.edit) $router.replace({ query: {} })">Cancel</button>
              </div>
            </div>
          </div>

          <div v-if="loading" class="card shadow-sm table-card"><div class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div></div>
          <div v-else-if="!diseases.length" class="card shadow-sm table-card">
            <div class="empty-state"><i class="fe fe-thermometer"></i>No diseases tracked yet.{{ canManage ? ' Click "Track a disease" to start.' : "" }}</div>
          </div>
          <div class="row">
            <div class="col-lg-6" v-for="d in diseases" :key="d.id">
              <div class="card shadow-sm table-card mb-4 disease-card">
                <div class="card-body">
                  <div class="d-flex align-items-start" style="gap: 10px;">
                    <div class="flex-grow-1">
                      <router-link :to="`/disease-tracking/${d.id}`"><h5 class="mb-1 font-weight-bold">{{ d.name }}</h5></router-link>
                      <div class="small text-muted">
                        {{ d.diagnoses.map((x) => x.name).join(", ") }}<span v-if="d.is_chronic"> · chronic</span>
                      </div>
                    </div>
                    <div class="d-flex flex-column align-items-end" style="gap: 6px;">
                      <DiseaseCategoryChip :classification="d.classification" />
                      <span class="sev" :class="levelClass(d.this_month.level)" :title="`Projected ${d.this_month.projected} this month; usual ${d.this_month.expected}`">
                        {{ levelLabel(d.this_month.level) }}
                      </span>
                    </div>
                  </div>
                  <div class="d-flex flex-wrap mt-3" style="gap: 20px;">
                    <div><span class="stat-label">Sufferers, 30 days</span><div class="stat-value">{{ d.last_30_days.sufferers.toLocaleString() }}</div></div>
                    <div><span class="stat-label">New</span><div class="stat-value text-danger">{{ d.last_30_days.new_sufferers.toLocaleString() }}</div></div>
                    <div><span class="stat-label">Claimed</span><div class="stat-value">₦{{ Math.round(d.last_30_days.amount_claimed).toLocaleString() }}</div></div>
                    <div><span class="stat-label">This month</span><div class="stat-value">{{ d.this_month.so_far }} <small class="text-muted">/ usual {{ d.this_month.expected }}</small></div></div>
                  </div>
                  <div class="small mt-2" v-if="d.danger_months.length"><i class="fe fe-alert-triangle text-warning mr-1"></i>Peaks in {{ d.danger_months.join(", ") }}</div>
                  <div class="small text-muted mt-1" v-if="d.last_alert"><i class="fe fe-bell mr-1"></i>{{ d.last_alert.summary }} ({{ String(d.last_alert.created_at).substr(0, 10) }})</div>
                  <div class="d-flex mt-3" style="gap: 6px;">
                    <router-link :to="`/disease-tracking/${d.id}`" class="btn btn-sm btn-primary">Open</router-link>
                    <button v-if="canManage" class="btn btn-sm btn-outline-secondary" @click="openForm(d)">Edit</button>
                    <button v-if="canManage" class="btn btn-sm btn-outline-danger ml-auto" @click="remove(d)">Stop tracking</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import DiseaseCategoryChip from "./components/DiseaseCategoryChip.vue";
import DiagnosisPicker from "./components/DiagnosisPicker.vue";

/** Tracked diseases (GET/POST/PUT/DELETE diseases, diseases/diagnoses, diseases/similar). ?edit=<id> opens its form. */
export default {
  components: { Navbar, DiseaseCategoryChip, DiagnosisPicker },
  data() {
    return { diseases: [], categories: [], canManage: false, loading: true, form: null, saving: false, loadingChosen: false, staff: [] };
  },
  mounted() { this.load(); },
  methods: {
    load() {
      this.loading = true;
      this.axios.get("/api/v1/auth/diseases")
        .then((res) => {
          this.diseases = res.data.data; this.canManage = res.data.can_manage; this.categories = res.data.categories || [];
          const edit = this.diseases.find((d) => String(d.id) === String(this.$route.query.edit || ""));
          if (edit && this.canManage && !this.form) this.openForm(edit);
        })
        .finally(() => { this.loading = false; });
    },
    openForm(d) {
      this.form = d
        ? { id: d.id, name: d.name, diagnoses: [], is_chronic: d.is_chronic, new_case_days: d.new_case_days,
          alert_new_cases: d.alert_new_cases, alert_spikes: d.alert_spikes, watchers: d.watchers || [], notes: d.notes, category: d.category || null }
        : { id: null, name: "", diagnoses: [], is_chronic: false, new_case_days: 365, alert_new_cases: true, alert_spikes: true, watchers: [], notes: "", category: null };
      if (d) this.loadChosen(d);
      this.loadStaff();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    // The disease's diagnoses as picker choices (typed ones grouped by name, with how often they were used).
    loadChosen(d) {
      const form = this.form;
      this.loadingChosen = true;
      this.axios.get("/api/v1/auth/diseases/diagnoses", { params: { ids: d.diagnosis_ids || [] } })
        .then((res) => { if (this.form === form) form.diagnoses = res.data.data; })
        .catch(() => this.$toasted.error("Could not load the disease's diagnoses.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.loadingChosen = false; });
    },
    loadStaff() {
      if (this.staff.length) return;
      const agency = JSON.parse(localStorage.getItem("agency") || "{}") || {};
      this.axios.get(`/api/v1/auth/getEmployee/${agency.id}`).then((res) => {
        const rows = Array.isArray(res.data) ? res.data : res.data.data || [];
        this.staff = rows.map((u) => ({ id: u.id, label: `${u.firstname || ""} ${u.lastname || ""}`.trim() + (u.job_title ? ` (${u.job_title})` : "") }));
      }).catch(() => {});
    },
    nameFromDiagnoses() {
      if (!this.form.name && this.form.diagnoses.length) this.form.name = this.form.diagnoses[0].name;
    },
    save() {
      this.saving = true;
      // Every record of each choice; the backend also counts records typed later with a typed diagnosis's name.
      const body = { ...this.form, diagnosis_ids: this.form.diagnoses.flatMap((d) => d.ids) };
      delete body.diagnoses;
      const req = this.form.id ? this.axios.put(`/api/v1/auth/diseases/${this.form.id}`, body) : this.axios.post("/api/v1/auth/diseases", body);
      req.then(() => { this.form = null; if (this.$route.query.edit) this.$router.replace({ query: {} }); this.load(); this.$toasted.success("Saved.", { position: "top-center", duration: 2500 }); })
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not save.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.saving = false; });
    },
    remove(d) {
      if (!confirm(`Stop tracking ${d.name}? Its alerts stop; the records themselves are not touched.`)) return;
      this.axios.delete(`/api/v1/auth/diseases/${d.id}`).then(() => this.load());
    },
    levelLabel(l) { return { high: "Higher than usual", elevated: "Above usual", normal: "Usual level" }[l] || l; },
    levelClass(l) { return { high: "sev-high", elevated: "sev-medium", normal: "sev-low" }[l]; },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
.disease-card h5 { color: #1e3a5f; }
.btn-xs { font-size: 0.75rem; }
</style>
